/*
 * AdvaHealth Solutions Pty. Ltd. ("AHS") CONFIDENTIAL
 * Copyright (c) 2022 AdvaHealth Solutions Pty. Ltd. All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains the property of AHS. The intellectual and technical concepts contained
 * herein are proprietary to AHS and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material is strictly forbidden unless prior written permission is obtained
 * from AHS.  Access to the source code contained herein is hereby forbidden to anyone except current AHS employees, managers or contractors who have executed
 * Confidentiality and Non-disclosure agreements explicitly covering such access.
 *
 * The copyright notice above does not evidence any actual or intended publication or disclosure  of  this source code, which includes
 * information that is confidential and/or proprietary, and is a trade secret, of AHS. ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC  PERFORMANCE,
 * OR PUBLIC DISPLAY OF OR THROUGH USE  OF THIS  SOURCE CODE  WITHOUT  THE EXPRESS WRITTEN CONSENT OF COMPANY IS STRICTLY PROHIBITED, AND IN VIOLATION OF APPLICABLE
 * LAWS AND INTERNATIONAL TREATIES.  THE RECEIPT OR POSSESSION OF  THIS SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS
 * TO REPRODUCE, DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT  MAY DESCRIBE, IN WHOLE OR IN PART.
 */
import {
  AdvaPACSPrivacyAndPolicy,
  AppLogo,
  TermsOfServiceLink,
} from "@advapacs/common";
import React, { FC, useMemo } from "react";
import { IRootState } from "@src/config/reducers";
import { useSelector } from "react-redux";
import { Link, useLocation } from "react-router-dom";
import ReportSidebarIcon from "../../assets/icons/report-filled.svg";
import AccSidebarIcon from "../../assets/icons/account-circle-filled.svg";
import AccIcon from "../../assets/icons/acc-circle.svg";
import ReportIcon from "../../assets/icons/report-outlined.svg";
import { useHeroLayout } from "@src/hooks/useHeroLayout";

interface IFooter {
  isAuthenticated?: boolean;
}

export const Footer: FC<IFooter> = ({ isAuthenticated = false }) => {
  const { pathname } = useLocation();
  const isHeroLayout = useHeroLayout();
  const { isMobileOnly, isMobile } = useSelector(
    (state: IRootState) => state.responsiveReducer
  );

  const showMainFooter = useMemo(() => {
    if (!isAuthenticated || (isAuthenticated && pathname.includes("/t/")))
      return true;

    return false;
  }, [isAuthenticated, pathname]);

  const showAuthenticatedSideBar = useMemo(() => {
    return (
      (isMobileOnly || isMobile) && isAuthenticated && !pathname.includes("/t/")
    );
  }, [isMobileOnly, isMobile, isAuthenticated, pathname]);

  return (
    <>
      {showMainFooter && (
        <div
          className={`navbar-text bg-white landing-footer ${
            !isAuthenticated ? "unauthenticated-footer" : "authenticated-footer"
          } ${isHeroLayout ? "footer-hero-layout" : "w-100 "}`}
        >
          <div className="w-100 text-center p-t-xs ">
            <AppLogo.LogoLong />
          </div>
          <p className=" w-100 text-center m-t-xxs text-label m-b-none ">
            <span>
              {" "}
              © {new Date().getFullYear()} AdvaPACS by{" "}
              <a href="https://adva.health" target="_blank">
                AdvaHealth Solutions
              </a>
            </span>{" "}
            {pathname === "/Dashboard" && (
              <>
                | <AdvaPACSPrivacyAndPolicy className="p-lr-xxs" />
                <>
                  |
                  <TermsOfServiceLink
                    className="p-l-xxs"
                    link={`${window.location.origin}/public/terms-of-service`}
                  />
                </>
              </>
            )}
          </p>
        </div>
      )}

      {showAuthenticatedSideBar && !pathname.endsWith("/terms-of-service") && (
        <div
          className={`d-flex bg-white justify-content-between border-top w-100 ${
            isMobile ? "border-top" : ""
          } landing-footer `}
        >
          <Link
            className={`nav-link p-t-xs p-b-none flex-1 d-block text-center ${
              window.location.href.includes("Exams") || pathname === "/Exams"
                ? "active exam-link"
                : ""
            } ${isMobile || isMobileOnly ? "fixed-h" : ""}`}
            to="/Exams"
          >
            <p className="m-b-none">
              <img
                src={
                  window.location.href.includes("MyAccount") ||
                  pathname === "/MyAccount"
                    ? ReportIcon
                    : ReportSidebarIcon
                }
              />
            </p>
            <p className="text-dark font-weight-600">Exams</p>
          </Link>

          <Link
            className={`nav-link p-t-xs p-b-none flex-1 d-block text-center ${
              window.location.href.includes("MyAccount") ||
              pathname === "/MyAccount"
                ? "active exam-link"
                : ""
            } ${isMobile || isMobileOnly ? "fixed-h" : ""}`}
            to="/MyAccount"
          >
            <p className="m-b-none text-dark">
              <img
                src={
                  window.location.href.includes("MyAccount") ||
                  pathname === "/MyAccount"
                    ? AccSidebarIcon
                    : AccIcon
                }
              />
            </p>
            <p className="text-dark font-weight-600">My Account</p>
          </Link>
        </div>
      )}
    </>
  );
};
